import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Icon } from '../components/icons';
import { Button, cx } from '../components/ui';
import { initials } from '../lib/format';

interface NavItem {
  to: string;
  label: string;
  icon: Parameters<typeof Icon>[0]['name'];
}

const ADMIN_NAV: NavItem[] = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: 'dashboard' },
  { to: '/admin/vehicles', label: 'Veículos', icon: 'car' },
  { to: '/admin/orders', label: 'Ordens', icon: 'wrench' },
  { to: '/admin/compras', label: 'Compras', icon: 'cart' },
  { to: '/admin/parts', label: 'Inventário', icon: 'box' },
  { to: '/admin/scanner', label: 'Scanner', icon: 'scan' },
  { to: '/admin/assistant', label: 'Assistente', icon: 'sparkles' },
  { to: '/admin/users', label: 'Usuários', icon: 'users' },
];

const SHOP_NAV: NavItem[] = [
  { to: '/shop', label: 'Ordens', icon: 'wrench' },
  { to: '/shop/compras', label: 'Compras', icon: 'cart' },
  { to: '/shop/scanner', label: 'Escanear', icon: 'scan' },
  { to: '/shop/parts', label: 'Peças', icon: 'box' },
];

function LoginScreen() {
  const { login } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="card p-8 max-w-sm w-full text-center space-y-5">
        <Brand large />
        <p className="text-muted text-sm">Entre com sua conta para acessar a garagem.</p>
        <Button className="w-full" size="lg" onClick={login}>
          Entrar
        </Button>
      </div>
    </div>
  );
}

function Brand({ large }: { large?: boolean }) {
  return (
    <div className="flex items-center gap-2.5 justify-center">
      <span className="text-accent">
        <Icon name="flag" className={large ? 'h-8 w-8' : 'h-6 w-6'} />
      </span>
      <div className="text-left leading-tight">
        <div className={cx('font-bold tracking-tight', large ? 'text-xl' : 'text-base')}>
          ETNRLZ <span className="text-accent">Garage</span>
        </div>
      </div>
    </div>
  );
}

export function AppShell({ mode }: { mode: 'admin' | 'shop' }) {
  const { user, ready, logout } = useAuth();
  const location = useLocation();

  if (!ready) {
    return <div className="min-h-screen flex items-center justify-center text-muted">Carregando…</div>;
  }
  if (!user) return <LoginScreen />;

  const nav = mode === 'shop' ? SHOP_NAV : ADMIN_NAV;
  const touch = mode === 'shop';

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Sidebar (desktop) / topbar (mobile) */}
      <aside
        className={cx(
          'bg-surface border-border md:border-r md:w-60 md:min-h-screen flex md:flex-col',
          'border-b md:border-b-0 shrink-0',
        )}
      >
        <div className="p-4 hidden md:block">
          <Brand />
        </div>
        <nav
          className={cx(
            'flex md:flex-col gap-1 p-2 md:px-3 overflow-x-auto flex-1',
            touch && 'gap-2',
          )}
        >
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/shop'}
              className={({ isActive }) =>
                cx(
                  'flex items-center gap-3 rounded-lg whitespace-nowrap transition-colors',
                  touch ? 'px-4 py-3 text-base' : 'px-3 py-2 text-sm',
                  isActive
                    ? 'bg-accent/10 text-accent font-medium'
                    : 'text-muted hover:text-text hover:bg-surface-2',
                )
              }
            >
              <Icon name={item.icon} className={touch ? 'h-6 w-6' : 'h-5 w-5'} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:flex flex-col gap-2 p-3 border-t border-border">
          <a
            href="/kiosk"
            className="flex items-center gap-2 text-xs text-muted hover:text-accent transition-colors px-3 py-2"
          >
            <Icon name="dashboard" className="h-4 w-4" /> Abrir Kiosk (TV)
          </a>
          <a
            href={mode === 'admin' ? '/shop' : '/admin'}
            className="flex items-center gap-2 text-xs text-muted hover:text-text transition-colors px-3 py-2"
          >
            <Icon name="wrench" className="h-4 w-4" /> Modo {mode === 'admin' ? 'Oficina' : 'Admin'}
          </a>
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="h-8 w-8 rounded-full bg-accent/15 text-accent grid place-items-center text-xs font-bold">
              {initials(user.nome)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{user.nome}</div>
              <div className="text-[11px] text-muted">{user.role}</div>
            </div>
            <button onClick={logout} className="text-muted hover:text-st-danger" title="Sair">
              <Icon name="logout" className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className={cx('flex-1 min-w-0', touch ? 'p-4 md:p-6' : 'p-5 md:p-8')} key={location.pathname}>
        <Outlet />
      </main>
    </div>
  );
}
